<template>
    <div ref="sessionRef"
         data-component="TodayViewLinearTableCellSession"
         class="linear-view-table__cell-session"
         :class="sessionClass"
         :style="session.styleObject"
         :data-analytics-id="`Today / Linear / Session ${session.ID}`"
         :data-testid="`cell-session-${session.ID}`"
         :title="combinedTime"
         @click.stop="setPopover">
    </div>
    <Tooltip v-if="availability && showPopover"
             ref="session-tooltip"
             :style-object="tooltipStyles"
             :arrow-position="arrowPosition">
        <div class="session-popover__info">
            <div class="session-tooltip_date mb-05">
                <template v-if="isToday">
                    {{ l('TODAY') }}
                </template>
                <template v-else>
                    {{ dateFilter(session.startTime) }}
                </template>
            </div>
            <div class="session-tooltip_times mb-05">
                <span v-html="startTime"></span> - <span v-html="endTime"></span>
            </div>
            <SessionSpaceCounts class="session-info__spaces"
                                :linear-mode="true"
                                :availability="availability" />
        </div>
        <div class="session-popover__buttons">
            <div class="session-popover__buttons_button"
                 :data-analytics-id="`Today / Linear / Session View Orders ${session.ID}`"
                 :data-testid="`today-linear-session-view-orders-${session.ID}`"
                 @click.prevent="viewOrdersForSession">
                <span>
                    <i class="fa-solid fa-eye mr-05"></i>
                    {{ l('TODAY_VIEW_TOOLTIP.VIEW_ORDERS') }}
                </span>
                <i class="fa-solid fa-chevron-right"></i>
            </div>
            <div v-if="canBookTickets"
                 class="session-popover__buttons_button"
                 :data-analytics-id="`Today / Linear / Session Book Tickets ${session.ID}`"
                 :data-testid="`today-linear-session-book-tickets-${session.ID}`"
                 @click.prevent="sellTicketsForSession">
                <span>
                    <i class="fa-solid fa-shopping-cart mr-05"></i>
                    {{ l('TODAY_VIEW_TOOLTIP.BOOK_TICKETS') }}
                </span>
                <i class="fa-solid fa-chevron-right"></i>
            </div>
        </div>
    </Tooltip>
</template>

<script>
import AppPath from '@/lib/Navigation/AppPath';
import Session from '@/lib/Sessions/Session';
import SessionSpaceCounts from '@/vue/components/Sessions/SessionSpaceCounts';
import SessionWithEvent from '@/lib/Sessions/SessionWithEvent';
import Tooltip from '@/vue/components/Tooltip';
import { container } from '@/container';
import { createSessionSalesCutoffFilterForEvent } from '@/lib/Sessions/sessionSalesCutoffFilter';
import { getTodayMidnightDate, toYMD } from '@/functions/date';
import { mapGetters } from 'vuex';
import { setTooltipPosition } from '../functions/todayViewLinearTableCellSessionFunctions';
import { timeFilter } from '@/vue/filters/timeFilter';
import { timeoutPromise } from '@/functions/functions';

export default {
    components: {
        SessionSpaceCounts,
        Tooltip,
    },

    props: {
        session: {
            type: Object,
            required: true,
        },
    },

    emits: ['popover-active'],

    data() {
        return {
            tooltipStyles: {},
            /** @type {'top-centre'|'top-left'|'top-right'|'bottom-centre'|'bottom-left'|'bottom-right'} */
            arrowPosition: 'top-centre',
            event: null,
            isStillBookable: false,
        };
    },

    computed: {
        ...mapGetters('today', ['sessionAvailability', 'getEvent']),

        availability() {
            return this.sessionAvailability(this.session.ID);
        },
        showPopover() {
            return this.$store.getters['today/isLinearPopoverActive'](this.session.ID);
        },
        startTime() {
            return this.formatTime(timeFilter(this.session.startTime));
        },
        endTime() {
            return this.formatTime(timeFilter(this.session.endTime));
        },
        combinedTime() {
            return timeFilter(this.session.startTime) + ' - ' + timeFilter(this.session.endTime);
        },
        canBookTickets() {
            // Is the session still bookable in terms of time and are there spaces still available?
            return this.isStillBookable && this.availability?.spacesAvailable > 0;
        },
        isToday() {
            return this.session?.startTimeYMD === toYMD(getTodayMidnightDate());
        },
        sessionClass() {
            let classString = 'linear-view-table__cell-session-past';

            if (this.canBookTickets) {
                classString = this.session.sessionClass;
            }

            if (this.showPopover) {
                classString += ' popover-active';
            }

            return classString;
        },
    },

    watch: {
        // Watch the value from the store, so we can close the popover if another one is opened.
        showPopover(newValue, oldValue) {
            // When transitioning from active to inactive.
            if (oldValue === true && newValue === false) {
                this.$emit('popover-active', false);
            }
        },
    },

    created() {
        this.event = this.getEvent(this.session.eventID);

        const sessionSalesCutoffFilter = createSessionSalesCutoffFilterForEvent(this.event);
        this.isStillBookable = sessionSalesCutoffFilter(this.session);
    },

    methods: {

        /**
         * @see TodayViewSession.viewOrdersForSession()
         *
         * @return {Promise<void>}
         */
        async viewOrdersForSession() {
            // We need an actual session here not just the data.
            const sessionWithEvent = new SessionWithEvent(new Session(this.session), this.event);

            await this.$store.dispatch(
                'orderSearch/setupSearchForSession',
                sessionWithEvent
            );

            container.navigationService.viewRedeem();
        },

        /**
         * @see TodayViewSession.sellTicketsForSession()
         *
         * @return {Promise<void>}
         */
        async sellTicketsForSession() {
            const newCart = container.cartService.createEmptyCart();
            const cartSet = await container.cartService.setNewCart(newCart, true);
            if (cartSet) {
                await container.cartService.modifyCurrentCart((currentCart) => {
                    currentCart.sellState.autoAddSessionID = this.session.ID;
                    currentCart.sellState.cancelTicketPickerReturnPath = new AppPath('/today');

                    if (container.currentDevice.device.manageGroupBookings) {
                        currentCart.sellState.addItemReturnPath = new AppPath('/today');
                        // Set to display a success message when the user adds an item to the cart.
                        currentCart.sellState.showItemAddedToast = true;
                    }
                });

                container.navigationService.viewSell();
            }
        },

        formatTime(str) {
            const elms = str.split(' ');
            return '<b>' + elms[0] + '</b> ' + elms[1];
        },

        /**
         * @param {PointerEvent} event
         */
        async setPopover(event) {
            if (this.showPopover) {
                // This is a click to close the popover.
                this.$store.commit('today/setActiveLinearPopover', null);
                this.$emit('popover-active', false);
                return;
            }

            // Prevent the click for a tiny pause as very rarely it can miscalculate if the user manages to click
            // while scrolling or at the end of a scroll.
            await timeoutPromise(50);

            // This is a click to open the popover.
            const data = {
                sessionID: this.session.ID,
            };
            this.$store.commit('today/setActiveLinearPopover', data);
            this.$emit('popover-active', true, event);

            const tableBoundingRect = document.getElementById('today-view__table-container').getBoundingClientRect();
            const firstColumnWidth = document.getElementById('today-view__table').firstChild.firstChild.offsetWidth;
            const { arrowPosition, tooltipStyles } = setTooltipPosition(event, tableBoundingRect, firstColumnWidth);
            this.arrowPosition = arrowPosition;
            this.tooltipStyles = tooltipStyles;
        },
    },
};
</script>

<style lang="less">
@import "../../../../../less/variables";
@import (less, reference) "../../../../../less/vendor/fontawesome.less";
.linear-view-table__cell-session-detail {
    width: 200px;
    height: 100px;
    background: @white;
    border-radius: 4px;
    border: 1px solid @default-lighter-grey;
}

.linear-view-table__cell-session {
    height: 40px;
    position: absolute;
    border-radius: 4px;
    padding: 0;
    top: 0;
}

.linear-view-table__cell-session-available {
    background: linear-gradient(90deg, @dt-green-800 5px, @dt-green-300 5px 100%);

    &:hover,
    &.popover-active{
        background: linear-gradient(90deg, @dt-green-800 5px, @dt-green-300-tint 5px 100%);
    }
}

.linear-view-table__cell-session-limited {
    background: linear-gradient(90deg, @orange-1 5px, @orange-1-tint 5px 100%);

    &:hover,
    &.popover-active {
        background: linear-gradient(90deg, @orange-1 5px, @orange-1-tint-2 5px 100%);
    }
}

.linear-view-table__cell-session-low {
    background: linear-gradient(90deg, @orange-2 5px, @orange-2-tint 5px 100%);

    &:hover,
    &.popover-active {
        background: linear-gradient(90deg, @orange-2 5px, @orange-2-tint-2 5px 100%);
    }
}

.linear-view-table__cell-session-very-low {
    background: linear-gradient(90deg, @dt-red-800 5px, @dt-red-300 5px 100%);

    &:hover,
    &.popover-active {
        background: linear-gradient(90deg, @dt-red-800 5px, @dt-red-500 5px 100%);
    }
}

.linear-view-table__cell-session-sold-out {
    background: linear-gradient(90deg, @dt-grey-neutral-800 5px, @dark-neutral-grey 5px 100%);

    &:hover,
    &.popover-active {
        background: linear-gradient(90deg, @dt-grey-neutral-800 5px, @dt-grey-neutral-700 5px 100%);
    }
}

.linear-view-table__cell-session-past {
    background: linear-gradient(90deg, @dt-grey-neutral-400 5px, @dt-grey-neutral-200 5px 100%);

    &:hover,
    &.popover-active {
        background: linear-gradient(90deg, @dt-grey-neutral-400 5px, @dt-grey-neutral-300 5px 100%);
    }
}

.chip {
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 0 22px 22px 0;
        border-top-right-radius: 4px;
    }
}

.chip__in-cart {
    &::after {
        border-color: transparent @dt-light-blue transparent transparent;
    }

    &::before {
        content: @fa-var-circle-check;
        font-family: 'Font Awesome 6 Pro';
        font-weight: 900;
        position: absolute;
        top: 2px;
        height: 10px;
        right: 1px;
        font-size: 9px;
        color: white;
        z-index: 1;
    }
}

.chip__group-booking {
    &::after {
        border-color: transparent @purple transparent transparent;
    }
}

.chip__inactive-event {
    &::after {
        border-color: transparent @dt-yellow-600 transparent transparent;
    }
}

.session-popover__info{
    background: white;
    padding: @gutter-width-narrow;
    text-align: center;
    .session-tooltip_date {
        font-size: @font-size-small;
        color: @darker-grey;
    }
    .session-tooltip_times {
        text-transform: lowercase;
        b {
            font-weight: 900;
        }
    }
    .session-info__spaces {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 4px;
        .session-space-count:not(:first-child) {
            margin: 0;
        }
    }
}
.session-popover__buttons {
    padding: 4px;
    border-top: 1px solid @btn-default-border;
    background: white;
    &_button {
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        border-radius: @border-radius-base;
        padding: @gutter-width-narrow;
        .fa-chevron-right {
            color: @default-grey;
        }
        &:hover {
            background: @light-grey;
        }
    }
}
</style>
